import React from 'react'
import PropTypes from 'prop-types'
import { GridWrapper, GridItem } from '@sainsburys-tech/luna-grid'
import { Body1, Caption, Display1 } from '@sainsburys-tech/luna-typography'

function SearchCalendarStatus(
  {
    loading, error, weeklyStatus, title
  }
) {
  if (loading) {
    return (
      <GridItem className="ln-u-hard-left u-text-center" size={{ default: '1/1' }}>
        <Caption>Checking search status...</Caption>
      </GridItem>
    )
  }

  if (error) {
    return (
      <GridItem className="ln-u-hard-left u-text-center" size={{ default: '1/1' }}>
        <Caption>Unable to check search status</Caption>
      </GridItem>
    )
  }
  const today = new Date().getDay()
  const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  const dayStatusMap = {
    Sun: 'sunday',
    Mon: 'monday',
    Tue: 'tuesday',
    Wed: 'wednesday',
    Thu: 'thursday',
    Fri: 'friday',
    Sat: 'saturday'
  }

  const getStatusMessage = () => {
    const fullDayNames = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday'
    ]
    const missedDays = []
    let allSearchesDone = true

    const dayValues = [
      weeklyStatus.sunday,
      weeklyStatus.monday,
      weeklyStatus.tuesday,
      weeklyStatus.wednesday,
      weeklyStatus.thursday,
      weeklyStatus.friday,
      weeklyStatus.saturday
    ]

    if (
      today === 0
      && dayValues.every((v) => v === false)
    ) {
      return <Body1>Searches must be conducted daily</Body1>
    }

    for (let i = 0; i < today; i += 1) {
      if (!weeklyStatus[dayStatusMap[dayLabels[i]]]) {
        allSearchesDone = false
        missedDays.push(fullDayNames[i])
      }
    }
    if (allSearchesDone) {
      const lastSearchDay = weeklyStatus[dayStatusMap[dayLabels[today]]] ? 'today' : 'yesterday'
      return (
        <Body1>
          Searches have been conducted daily, your last search was
          {' '}
          <strong>{lastSearchDay}</strong>
        </Body1>
      )
    }

    const missedDaysExcludingToday = missedDays.filter(
      (day) => day !== fullDayNames[today]
    )
    if (missedDaysExcludingToday.length > 0) {
      if (missedDaysExcludingToday.length === 1) {
        return (
          <Body1>
            Your store missed conducting searches on
            {' '}
            <strong>{missedDaysExcludingToday[0]}</strong>
            , searches must be conducted daily
          </Body1>
        )
      }

      if (missedDaysExcludingToday.length === 2) {
        return (
          <Body1>
            Your store missed conducting searches on
            {' '}
            <strong>{missedDaysExcludingToday[0]}</strong>
            {' '}
            and
            {' '}
            <strong>{missedDaysExcludingToday[1]}</strong>
            , searches must be conducted daily
          </Body1>
        )
      }

      const lastDay = missedDaysExcludingToday.pop()
      const formattedDays = missedDaysExcludingToday.map((day) => (
        <strong key={day}>{day}</strong>
      ))

      const result = []
      formattedDays.forEach((day, index) => {
        result.push(day)
        if (index < formattedDays.length - 1) {
          result.push(', ')
        }
      })

      return (
        <Body1>
          Your store missed conducting searches on
          {' '}
          {result}
          {' '}
          and
          {' '}
          <strong>{lastDay}</strong>
          , searches must be conducted daily
        </Body1>
      )
    }
    return <Body1>Searches must be conducted daily</Body1>
  }
  return (
    <GridWrapper className="ln-u-flush-left">
      <GridItem className="ln-u-hard-left ln-u-margin-top*3" size={{ default: '1/1' }}>
        {title && (
          <Display1 className="search-calendar__title">
            {title}
          </Display1>
        )}
        <div
          className="search-calendar__container"
          data-testid="search-calendar-container"
        >
          {dayLabels.map((day, index) => {
            const dayIndex = dayLabels.indexOf(day)
            const isToday = dayIndex === today
            const hasSearches = weeklyStatus[dayStatusMap[day]]
            const isFutureDay = dayIndex > today

            const dayBoxClasses = [
              'search-calendar__day-box',
              !hasSearches && isToday && 'search-calendar__day-box--today',
              isFutureDay && 'search-calendar__day-box--future',
              hasSearches && 'search-calendar__day-box--completed',
              !hasSearches && !isFutureDay && !isToday && 'search-calendar__day-box--missed'
            ].filter(Boolean).join(' ')

            return (
              <div
                key={`day-${day}`}
                className={dayBoxClasses}
                data-testid={
                  isToday ? 'today-day-indicator' : `day-indicator-${dayIndex}`
                }
              >
                {isToday && <div className="search-calendar__today-indicator" />}
                {day}
              </div>
            )
          })}
          {' '}
        </div>
        <div className="search-calendar__status-label" data-testid="search-status-label">
          {getStatusMessage()}
        </div>
      </GridItem>
    </GridWrapper>
  )
}

SearchCalendarStatus.propTypes = {
  loading: PropTypes.bool.isRequired,
  error: PropTypes.bool,
  weeklyStatus: PropTypes.shape({
    sunday: PropTypes.bool,
    monday: PropTypes.bool,
    tuesday: PropTypes.bool,
    wednesday: PropTypes.bool,
    thursday: PropTypes.bool,
    friday: PropTypes.bool,
    saturday: PropTypes.bool,
    weekStartDate: PropTypes.string
  }),
  title: PropTypes.string
}

SearchCalendarStatus.defaultProps = {
  error: false,
  title: '',
  weeklyStatus: {
    sunday: false,
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false
  }
}

export default SearchCalendarStatus
